import { Tag } from 'antd';
import clsx from 'clsx';
import { findIndex, isNaN } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../AppContext';
import { ForwardArrow, ProjectIcon } from '../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../common/CommonUseLazyQuery';
import {
  BREAKPOINTS,
  REGEX,
  REGISTER_LOG_STATUS,
  ROUTES,
  STAGE_STATUS_CLASSNAME,
  TAB_KEYS,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import { titleCase } from '../../../../../../common/utils';
import EllipsisText from '../../../../../../components/EllipsisText';
import Error404 from '../../../../../../Error404';
import history from '../../../../../../historyData';
import {
  GET_REGISTER,
  GET_REGISTER_LOG_HISTORIES,
  GET_SINGLE_PROJECT,
} from '../../../../graphql/Queries';
import RegisterPropertyDetails from './registerDetails/RegisterPropertyDetails';

const RegisterDetailsContent = () => {
  const { getCurrentUser, dispatch } = useContext(AppContext);
  const currentUserId = getCurrentUser()?.id || {};
  const {
    navigate,
    location,
    params: { registerId: pramsRegisterId, projectId, registerLogId },
  } = useRouter();
  const [isUpdate, setIsUpdate] = useState(false);
  const navFilter = location?.state?.navFilter;
  const navPagination = location?.state?.navPagination;
  const currentPath = location?.state?.currentPath;
  const registerNavFilter = location?.state?.registerNavFilter;
  const searchValue = location?.state?.searchValue;
  const registerDetailsSearchValue =
    location?.state?.registerDetailsSearchValue;
  const registerLogColumnFilter =
    location?.state?.registerLogColumnFilter;
  const registerLogSortedInfo =
    location?.state?.registerLogSortedInfo;

  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  // History dropdown state
  const [historyOptions, setHistoryOptions] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState();
  const [historyData, setHistoryData] = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [registerLog, setRegisterLog] = useState(null);
  const [registerId, setRegisterId] = useState();

  useEffect(() => {
    if (location?.pathname?.includes('/edit-control')) {
      setRegisterId(pramsRegisterId);
    }
  }, [location?.pathname]);

  const [getWebProject, { data: projectData }] = useLazyQueryWithCallbacks(
    GET_SINGLE_PROJECT,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        dispatch({
          type: 'SET_PROJECT_DETAILS',
          data: JSON.stringify(res?.getProject),
        });
        const index = findIndex(
          res?.getProject?.projectUsers,
          (user) => Number(user?.userId) === Number(currentUserId),
        );
        if (index !== -1) {
          const loggedInUserRole =
            res?.getProject?.projectUsers?.[index]?.roles;
          dispatch({ type: 'SET_PROJECT_ROLE', data: loggedInUserRole });
        } else {
          dispatch({ type: 'SET_PROJECT_ROLE', data: '' });
        }
      },
    },
  );

  const [getRegister, { data: registerData }] = useLazyQueryWithCallbacks(
    GET_REGISTER,
    {
      fetchPolicy: 'network-only',
      onError: () => {},
    },
  );

  const [getRegisterLogHistories] = useLazyQueryWithCallbacks(
    GET_REGISTER_LOG_HISTORIES,
    {
      fetchPolicy: 'network-only',
      onError() {
        setHistoryLoading(false);
      },
      onCompleted(res) {
        const histories = res?.getRegisterLogHistories?.data || [];
        const registerLogData = res?.getRegisterLogHistories?.registerLog;
        const fetchedRegisterId = res?.getRegisterLogHistories?.registerId;

        setHistoryOptions(histories);
        setRegisterLog(registerLogData);

        // Update registerId from the response
        if (fetchedRegisterId) {
          setRegisterId(fetchedRegisterId);
        }

        // Auto-select the latest history entry if available
        if (histories?.length > 0) {
          const latestHistory =
            histories?.find((h) => h?.isLatest) || histories[0];
          setSelectedHistoryId(latestHistory?.id);
          setHistoryData(latestHistory);
        }

        setHistoryLoading(false);
      },
    },
  );

  useEffect(() => {
    const unlisten = history.listen((props) => {
      if (props.action === 'POP') {
        navigate(props.location.pathname, {
          state: {
            navFilter,
            navPagination,
            registerNavFilter,
            searchValue,
            registerDetailsSearchValue,
            registerLogColumnFilter,
            registerLogSortedInfo,
          },
        });
      }
    });
    return () => {
      if (unlisten) {
        unlisten();
      }
    };
  }, []);

  useEffect(() => {
    if (!isNaN(Number(projectId))) {
      getWebProject({
        variables: {
          id: projectId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Initialize history data when component mounts (only when registerLogId exists)
  useEffect(() => {
    if (
      location?.pathname !==
        `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control` &&
      registerLogId
    ) {
      setHistoryLoading(true);
      getRegisterLogHistories({
        variables: { registerLogId },
      });
    }
  }, [registerLogId, location?.pathname, projectId]);

  const handleHisotryRefetch = () => {
    getRegisterLogHistories({
      variables: { registerLogId },
    });
  };

  useEffect(() => {
    if (registerId) {
      getRegister({
        variables: { getRegisterId: registerId },
      });
    }
  }, [registerId]);

  // Update historyData when selectedHistoryId changes
  useEffect(() => {
    if (selectedHistoryId) {
      const index = findIndex(
        historyOptions,
        ({ id }) => selectedHistoryId === id,
      );
      if (index !== -1) {
        setHistoryData(historyOptions[index] || {});
      }
    }
  }, [selectedHistoryId, historyOptions]);

  useEffect(() => {
    if (
      location?.pathname ===
      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
    ) {
      setIsUpdate(true);
    }
  }, [location?.pathname, registerId]);

  const handleHistorySelect = (value) => {
    setSelectedHistoryId(value);
    // Find and set the selected history data
    const selectedHistory = historyOptions?.find((h) => h?.id === value);
    if (selectedHistory) {
      setHistoryData(selectedHistory);
    }
  };

  if (registerLogId && !REGEX.REGISTER_UUID?.test(registerLogId)) {
    return <Error404 />;
  }

  return (
    <div className="project-details">
      <div
        className={`project-details-title details-header justify-between page-header ${
          isDesktopViewport ? 'd-flex' : ''
        }`}
      >
        <div className="project-details-title-text d-flex align-center">
          <ProjectIcon
            onClick={() => {
              navigate(ROUTES.PROJECTS, {
                state: {
                  navFilter,
                  navPagination,
                  registerNavFilter,
                  registerId,
                  searchValue,
                  registerDetailsSearchValue,
                  registerLogColumnFilter,
                  registerLogSortedInfo,
                },
              });
            }}
            className="pointer"
            id="register-projects-icon"
          />
          <ForwardArrow />
          <h1
            className="pointer mr-10 text-primary"
            onClick={() => {
              navigate(`${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}`, {
                state: {
                  navFilter,
                  navPagination,
                  currentPath,
                  ...{
                    registerId:
                      registerData?.getRegister?.parentRegisterId || registerId,
                  },
                  registerNavFilter,
                  searchValue,
                  registerDetailsSearchValue,
                  registerLogColumnFilter,
                  registerLogSortedInfo,
                },
              });
            }}
            id="register-project-name"
          >
            {projectData?.getProject?.name}
          </h1>
          {!isUpdate ? (
            <>
              <ForwardArrow />
              <EllipsisText
                className="h1-typography"
                text={registerData?.getRegister?.name}
              />
            </>
          ) : (
            <>
              <ForwardArrow />
              <EllipsisText className="h1-typography" text="Edit Register" />
            </>
          )}
          {isDesktopViewport &&
            registerLog?.status &&
            registerLog?.currentApprovalName && (
              <div className="d-flex align-center ml-10">
                ( <EllipsisText text={registerLog?.currentApprovalName} />
                &nbsp;-&nbsp;
                <Tag
                  className={`${STAGE_STATUS_CLASSNAME[registerLog?.status]} mr-0`}
                >
                  {titleCase(
                    REGISTER_LOG_STATUS[
                      registerLog?.status === REGISTER_LOG_STATUS.REDO_DRAFT
                        ? REGISTER_LOG_STATUS?.REDO
                        : registerLog?.status
                    ],
                  )}
                </Tag>
                {registerLog?.status === REGISTER_LOG_STATUS.REDO_DRAFT && (
                  <div className="ml-2">(Draft)</div>
                )}
                )
              </div>
            )}
        </div>
        <div id="save-as-draft" />
      </div>
      {!isDesktopViewport &&
        registerLog?.status &&
        registerLog?.currentApprovalName && (
          <div className="d-flex justify-center align-center ml-10">
            ( <EllipsisText text={registerLog?.currentApprovalName} />
            &nbsp;-&nbsp;
            <Tag
              className={`${STAGE_STATUS_CLASSNAME[registerLog?.status]} mr-0`}
            >
              {titleCase(
                REGISTER_LOG_STATUS[
                  registerLog?.status === REGISTER_LOG_STATUS.REDO_DRAFT
                    ? REGISTER_LOG_STATUS?.REDO
                    : registerLog?.status
                ],
              )}
            </Tag>
            {registerLog?.status === REGISTER_LOG_STATUS.REDO_DRAFT && (
              <div className="ml-2">(Draft)</div>
            )}
            )
          </div>
        )}
      <div className={clsx(isDesktopViewport ? 'mt-25' : 'p-14')}>
        <RegisterPropertyDetails
          registerDetailsData={registerData?.getRegister}
          registerId={registerId}
          historyOptions={historyOptions}
          selectedHistoryId={selectedHistoryId}
          historyData={historyData}
          historyLoading={historyLoading}
          registerLog={registerLog}
          handleHistorySelect={handleHistorySelect}
          handleHisotryRefetch={handleHisotryRefetch}
        />
      </div>
    </div>
  );
};

export default RegisterDetailsContent;
